import { Mountain, FileSignature, Handshake, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Mountain,
    tag: "G1 · G2 · G3 · G4 · G5",
    title: "Études de sol",
    desc: "Analyse complète de votre terrain pour adapter votre projet et éviter les problèmes de fondation.",
    bullets: [
      "Mission G1 (étude préalable)",
      "Mission G2 (étude de conception)",
      "Missions G3 à G5 (suivi de réalisation)",
      "Étude SPANC pour assainissement non collectif",
    ],
    result: "Des fondations adaptées au terrain, zéro mauvaise surprise.",
  },
  {
    icon: FileSignature,
    tag: "Dossier administratif",
    title: "Permis de construire",
    desc: "Création d'un dossier complet, conforme et prêt à être déposé en mairie.",
    bullets: [
      "Plans permis de construire",
      "Notices, CERFA, pièces obligatoires",
      "Insertion paysagère & 3D",
      "Suivi d'instruction en mairie",
    ],
    result: "Un permis accepté, sans aller-retour avec la mairie.",
  },
  {
    icon: Handshake,
    tag: "De A à Z",
    title: "Accompagnement complet",
    desc: "Un seul interlocuteur pour gérer votre projet de A à Z et vous faire gagner du temps.",
    bullets: [
      "Pilotage global du projet",
      "Coordination conception × technique",
      "Réseau de partenaires locaux",
      "Gain de temps mesurable",
    ],
    result: "Plusieurs semaines gagnées, et un projet qui avance droit.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32"
    >
      <div className="container-wide">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-7 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule" />
              <span className="overline">Nos services</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-medium leading-tight text-primary">
              Trois expertises,{" "}
              <span className="italic font-light">un seul interlocuteur</span>.
            </h2>
          </div>
          <div className="md:col-span-5 reveal">
            <p className="text-base text-muted-foreground leading-relaxed">
              Bureau d'études et accompagnement de projet à La Réunion.
              Nous prenons en charge l'étude technique du terrain, l'élaboration
              du dossier de permis de construire, et le pilotage global de
              votre projet.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`service-card-${i}`}
                className="reveal group relative bg-white border border-border p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] hover:shadow-[0_24px_60px_-24px_rgba(47,45,42,0.22)]"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <ArrowUpRight
                    size={20}
                    strokeWidth={1.4}
                    className="text-muted-foreground/40 transition-all duration-300 group-hover:text-[hsl(var(--brand-terra))] group-hover:-translate-y-1 group-hover:translate-x-1"
                  />
                </div>

                <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))]">
                  {s.tag}
                </p>
                <h3 className="mt-3 font-display text-2xl md:text-3xl text-primary leading-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>

                <ul className="mt-7 space-y-2.5 border-t border-border pt-6">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-sm text-primary/85"
                    >
                      <span className="mt-2 h-1 w-3 bg-[hsl(var(--brand-terra))] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-7 -mx-8 md:-mx-10 -mb-8 md:-mb-10 px-8 md:px-10 py-5 bg-[hsl(var(--brand-terra)/0.06)] border-t border-[hsl(var(--brand-terra)/0.18)]">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))]">
                    Résultat
                  </p>
                  <p className="mt-1.5 text-sm font-medium text-primary leading-snug">
                    {s.result}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs uppercase tracking-[0.22em] text-muted-foreground reveal">
          Études réalisées avec notre partenaire expert ·{" "}
          <a
            href="https://www.innocean.re/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[hsl(var(--brand-terra))] font-semibold hover:underline"
          >
            Innocéan
          </a>
        </p>
      </div>
    </section>
  );
}
